<template>
  <div
    id="infuse-navigation-sidebar"
    class="hidden h-screen max-h-screen w-[15.625rem] flex-col overflow-y-hidden bg-neutral-50 py-8 px-2 shadow-xs lg:flex"
  >
    <nav class="flex-initial px-4">
      <router-link to="/">
        <div class="h-12 w-full bg-company-logo bg-contain bg-center bg-no-repeat"></div>
      </router-link>

      <div class="pointer-events-none border-b border-neutral-100 pt-4" role="presentation"></div>
    </nav>

    <div class="flex flex-1 grow flex-col justify-between overflow-y-auto py-6">
      <div class="flex flex-col gap-2">
        <div
          v-for="(groupItems, groupKey) in menuItems.reduce((groups: any, item: MenuItem) => {
            const groupName = item.group || 'ungrouped';
            if (!groups[groupName]) {
              groups[groupName] = [];
            }
            groups[groupName].push(item);
            return groups;
          }, {})"
          :key="groupKey"
          class="group-container"
        >
          <nav v-permission="{ ids: groupItems.flatMap((item: MenuItem) => item.permissionIds), grantFalsyIds: true }">
            <NavigationSideBarItemGroup :group="groupKey.toString()" :group-items="groupItems" />
          </nav>
        </div>
      </div>

      <div>
        <component :is="optionalMFAComponent" :email="user?.email">
          <template #mfaMenu="{ onClick }">
            <button
              type="button"
              class="group flex w-full cursor-pointer items-center rounded-md py-2 px-4 text-left transition-colors duration-150 ease-out-quart hover:bg-neutral-100/70"
              @click="onClick"
            >
              <component
                :is="UserIcon"
                class="mr-2 h-5 w-5 text-neutral-700 transition-colors duration-150 ease-out-quart group-hover:text-primary-500"
              />

              <span
                class="text-sm font-normal tracking-wide text-neutral-700 transition-colors duration-150 ease-out-quart group-hover:text-primary-500"
              >
                {{ t('nav.mfa') }}
              </span>
            </button>
          </template>
        </component>
        <div v-if="showSettings" class="flex flex-initial flex-col gap-2">
          <button
            type="button"
            class="group flex w-full cursor-pointer items-center rounded-md py-2 px-4 text-left transition-colors duration-150 ease-out-quart hover:bg-neutral-100/70"
            @click="showSettingsModal"
          >
            <component
              :is="Settings"
              class="mr-2 h-5 w-5 text-neutral-700 transition-colors duration-150 ease-out-quart group-hover:text-primary-500"
            />

            <span
              class="text-sm font-normal tracking-wide text-neutral-700 transition-colors duration-150 ease-out-quart group-hover:text-primary-500"
            >
              {{ t('nav.settings') }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="user && showLogout" class="flex w-full flex-col gap-4 px-2">
      <div class="flex items-center border-t border-neutral-100 pt-4">
        <InfuseAvatar :avatar="user?.avatar" :email="user?.email" class="mr-3" />

        <div class="mr-3 flex flex-1 flex-col overflow-hidden">
          <p class="overflow-hidden text-ellipsis text-xs font-semibold tracking-wide text-secondary-700">
            {{ user?.fullName || t('nav.welcome_back') }}
          </p>
          <p class="text-secondary/[70%] overflow-hidden text-ellipsis text-xs">{{ user?.email }}</p>
        </div>

        <InfuseIconButton
          :icon="['far', 'arrow-right-from-bracket']"
          :aria-label="t('nav.log_out')"
          class="cursor-pointer text-neutral-600 transition-colors duration-150 ease-out-quart hover:text-secondary-700"
          size="1x"
          @click="$emit('logout')"
        />
      </div>
    </div>

    <div v-if="$slots['sidebar-footer']" class="-mb-8 mt-auto border-t border-neutral-100 px-4 pt-4 pb-3">
      <slot name="sidebar-footer" />
    </div>
  </div>

  <SettingsModal v-if="triggerSettingsModalVisible" @close="triggerSettingsModalVisible = false" />
</template>

<script lang="ts" setup>
import { User } from '@infuse/auth/services';
import { InfuseAvatar, InfuseIconButton, SettingsModal } from '@infuse/ui/components';
import { MenuItem } from '@infuse/ui/domain';
import { computed, getCurrentInstance, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Settings from '../assets/infuse-icons/settings.svg?component';
import UserIcon from '../assets/infuse-icons/lock.svg?component';

import NavigationSideBarItemGroup from './NavigationSideBarItemGroup.vue';
import { vPermission } from '@infuse/iam/permission/directives';

interface Props {
  menuItems: MenuItem[];
  user?: User;
  settings?: boolean;
  showSettings?: boolean;
  /**
   * Whether to render the user block and its logout button. EVE Control moves both to
   * its topbar. Defaults to true so every other app is unaffected.
   */
  showLogout?: boolean;
}

const instance = getCurrentInstance();
const registry = instance?.appContext?.components ?? {};

const optionalMFAComponent = computed(() => registry['OptionalMFA'] ?? undefined);

const triggerSettingsModalVisible = ref(false);
const { t } = useI18n();

function showSettingsModal() {
  triggerSettingsModalVisible.value = true;
}

withDefaults(defineProps<Props>(), { showLogout: true });
defineEmits(['logout']);
</script>

<i18n>
{
  "en": {
    "nav": {
      "welcome_back": "Welcome back!",
      "settings": "Settings"
    }
  },
  "nl": {
    "nav": {
      "welcome_back": "Welkom terug!",
      "settings": "Instellingen"
    }
  }
}
</i18n>
